import { api } from './api'
import type {
    Bill,
    BillsResponse,
    ListBillsParams,
    ProcessBillActionRequest
} from '@/types/bill'
import type { CreateJobResponse } from '@/types/job'
import { useMutation } from '@tanstack/react-query'

// The Bill type already exists in @/types/bill.ts and uses snake_case
// No need for transformation - API returns snake_case directly

// AI Review response type definition
export interface AiReviewData {
  bill_number: string;
  session_code: string;
  version: string;
  action: string;
  report_umd: {
    impact_category: string;
    rationale: string;
    impacts: Array<{
      description: string;
      rating: string;
      sentiment: string;
      bill_text: string;
      citation: string;
    }>;
  };
  report_higher_ed: {
    impacts: Array<{
      description: string;
      rating: string;
      sentiment: string;
      bill_text: string;
      citation: string;
    }>;
    summary: string;
  };
  [key: string]: unknown; // Allow for additional properties
}


export const billsApi = {
    getBill: async (session_code: string, bill_number: string): Promise<Bill> => {
        try {
            const encodedSession = encodeURIComponent(session_code)
            const response = await api.get<Bill>(`/session/${encodedSession}/bills/${bill_number}`, {
                is_workspace_specific: true
            })
            return response.data
        } catch (error) {
            console.error('Failed to fetch bill:', error)
            throw error
        }
    },
    listBills: async ({next_token, min_impact_score, session}: ListBillsParams): Promise<BillsResponse> => {
        const params = new URLSearchParams()
        if (next_token) {
            const tokenStr = typeof next_token === 'object' ? JSON.stringify(next_token) : next_token
            params.append('next_token', tokenStr)
        }
        
        // Add impact score filtering
        if (min_impact_score !== undefined) {
            params.append('min_impact_score', min_impact_score.toString())
            console.log(`[billsApi] Filtering by min_impact_score=${min_impact_score}`)
        }
        
        // Add session parameter
        if (session) {
            params.append('session', session)
            console.log(`[billsApi] Using session=${session}`)
        }

        try {
            const requestUrl = `/bills?${params.toString()}`
            console.log(`[billsApi] Making request to: ${requestUrl}`)
            
            // Make the API request
            const response = await api.get<BillsResponse>(requestUrl, {
                is_workspace_specific: true
            })
            console.log(`[billsApi] Response status: ${response.status}`)
            console.log('Raw response data:', response.data)
            console.log(`[billsApi] Response: ${response.data.items.length} items`)
            return response.data
        } catch (error) {
            console.error('Failed to fetch bills:', error instanceof Error ? error.message : String(error))
            throw error
        }
    },
    processBillAction: async ({bill_number, action, session}: ProcessBillActionRequest): Promise<CreateJobResponse> => {
        // This maps to /org/{org_id}/ws/{ws_id}/jobs/process-bills/{bill_number}
        const params = new URLSearchParams()
        params.append('bill_action', action)
        if (session) {
            params.append('session', session)
        }
        
        const response = await api.post<CreateJobResponse>(
            `/jobs/process-bills/${bill_number}?${params.toString()}`,
            undefined,
            { is_workspace_specific: true }
        )
        return response.data
    },
    getAIReview: async (session: string, bill_number: string): Promise<AiReviewData> => {
        try {
            const encodedSession = encodeURIComponent(session)
            const response = await api.get<AiReviewData>(`/session/${encodedSession}/bills/${bill_number}/ai-review`, {
                is_workspace_specific: true
            })
            
            let data = response.data as Record<string, unknown> & Partial<AiReviewData>;
            
            // Handle case where data is wrapped in an analysis object with raw_output
            const analysisObj = data.analysis as {raw_output?: string, report_umd?: unknown, report_higher_ed?: unknown} | undefined;
            if (analysisObj && analysisObj.raw_output) {
                try {
                    // Extract JSON from raw_output (remove ```json and ``` wrapper if present)
                    const rawOutput = analysisObj.raw_output;
                    const jsonMatch = rawOutput.match(/```json\s*(.*?)\s*```/s);
                    const jsonString = jsonMatch ? jsonMatch[1] : rawOutput;
                    
                    // Parse the extracted JSON and merge with top-level properties
                    const parsedData = JSON.parse(jsonString);
                    data = {
                        ...data,
                        ...parsedData
                    };
                    
                    // Remove the analysis wrapper since we've extracted the data
                    delete data.analysis;
                } catch (parseError) {
                    console.warn('Failed to parse raw_output JSON:', parseError);
                    // Fall back to original data if parsing fails
                }
            }
            // Handle case where report data is nested under analysis (most common case)
            else if (analysisObj && (analysisObj.report_umd || analysisObj.report_higher_ed)) {
                // Extract report data from analysis and merge with top-level properties
                const analysisData = analysisObj;
                data = {
                    ...data,
                    ...(analysisData as Record<string, unknown>)
                };
                
                // Remove the analysis wrapper since we've extracted the data
                delete data.analysis;
            }
            
            return data as AiReviewData;
        } catch (error) {
            console.error('Failed to fetch AI review:', error)
            throw error
        }
    }
}
export const useGetAIReview = () => {
    return useMutation({
        mutationFn: ({ session, bill_number }: { session: string, bill_number: string }) =>
            billsApi.getAIReview(session, bill_number)
    })
}
